import { useState, useEffect, useCallback, FC } from 'react'
import { Alert, Slide } from '@mui/material'

import { useSnackbarContext, ToastProps } from './SnackbarProvider.js'
import { Horizontal, Vertical } from './types.js'

type SnackbarToastProps = {
  toast: ToastProps
}

const getDirection = (position: { vertical: Vertical; horizontal: Horizontal }) => {
  if (position.horizontal === 'center') {
    return position.vertical === 'top' ? 'down' : 'up'
  }

  if (position.horizontal === 'left') {
    return 'right'
  }

  return 'left'
}

export const SnackbarToast: FC<SnackbarToastProps> = ({ toast }) => {
  const [open, setOpen] = useState(toast.visible)
  const { removeToast } = useSnackbarContext()

  const close = useCallback(() => {
    setOpen(false)
  }, [])

  useEffect(() => {
    if (toast.duration !== 0) {
      setTimeout(() => {
        close()
      }, toast.duration || 6000)
    }
  }, [close, toast.duration])

  useEffect(() => {
    if (!toast.visible) {
      close()
    }
  }, [toast.visible, close])

  const handleAnimationExit = () => removeToast(toast.key)

  const handleClose = (_: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return
    }
    close()
  }

  return (
    <Slide
      in={open}
      timeout={toast.disableAnimation ? 0 : toast.transitionDuration}
      onExited={handleAnimationExit}
      direction={getDirection(toast.position)}
    >
      <Alert
        className={toast.visible ? `SnackbarItem-message` : ``}
        key={toast.key}
        severity={toast.severity}
        onClose={toast.withCloseButton ? handleClose : undefined}
        onClick={handleClose}
        variant="filled"
        action={toast.action}
        sx={{
          border: 'none',
          '& .MuiButtonBase-root': {
            color: 'inherit',
          },
        }}
      >
        {toast?.message}
      </Alert>
    </Slide>
  )
}
